import React, { useState } from 'react';
import { 
  Box, Typography, Paper, Grid, Button, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Card, CardContent, CardMedia, TextField, Chip, IconButton, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';

// Mock blog posts data
const blogPosts = [
  { 
    id: 1, 
    title: 'Introduction to AI Trading', 
    excerpt: 'Learn how artificial intelligence is revolutionizing the world of trading.',
    author: 'Emma Johnson',
    category: 'Education',
    tags: ['AI', 'Trading', 'Machine Learning'],
    published: '2023-05-10',
    status: 'Published',
    image: 'https://source.unsplash.com/random/800x600/?trading'
  },
  { 
    id: 2, 
    title: 'Risk Management Strategies', 
    excerpt: 'Effective risk management is crucial for successful trading. Here are key strategies.',
    author: 'Michael Chen',
    category: 'Strategy',
    tags: ['Risk Management', 'Trading', 'Strategy'],
    published: '2023-05-05',
    status: 'Published',
    image: 'https://source.unsplash.com/random/800x600/?chart'
  },
  { 
    id: 3, 
    title: 'Technical Analysis Fundamentals', 
    excerpt: 'Master the basics of technical analysis to improve your trading decisions.',
    author: 'James Brown',
    category: 'Education',
    tags: ['Technical Analysis', 'Charts', 'Patterns'],
    published: '2023-04-28',
    status: 'Published',
    image: 'https://source.unsplash.com/random/800x600/?analysis'
  },
  { 
    id: 4, 
    title: 'Upcoming Trading Platform Features', 
    excerpt: 'Discover the exciting new features coming to our trading platform.',
    author: 'Emma Johnson',
    category: 'News',
    tags: ['Features', 'Updates', 'Platform'],
    published: null,
    status: 'Draft',
    image: 'https://source.unsplash.com/random/800x600/?technology'
  }
];

// Blog categories
const categories = [
  'Education', 'Strategy', 'News', 'Tutorials', 'Case Studies', 'Market Updates'
];

function Blog() {
  const [open, setOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [view, setView] = useState('grid'); // 'grid' or 'list'

  const handleOpen = (post = null) => {
    setSelectedPost(post);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedPost(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Blog Management</Typography>
        <Box>
          <Button 
            variant="contained" 
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpen()}
            sx={{ mr: 2 }}
          >
            New Post
          </Button>
          <Button 
            variant={view === 'grid' ? 'contained' : 'outlined'}
            color="secondary"
            onClick={() => setView('grid')}
            sx={{ mr: 1 }}
          >
            Grid
          </Button>
          <Button 
            variant={view === 'list' ? 'contained' : 'outlined'}
            color="secondary"
            onClick={() => setView('list')}
          >
            List
          </Button>
        </Box>
      </Box>

      {view === 'grid' ? (
        <Grid container spacing={3}>
          {blogPosts.map((post) => (
            <Grid item xs={12} md={6} lg={4} key={post.id}>
              <Card variant="outlined">
                <CardMedia
                  component="img"
                  height="140"
                  image={post.image}
                  alt={post.title}
                />
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Chip 
                      label={post.category} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                    <Chip 
                      label={post.status} 
                      size="small" 
                      color={post.status === 'Published' ? 'success' : 'default'} 
                    />
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    {post.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {post.excerpt}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {post.tags.map((tag) => (
                      <Chip 
                        key={tag} 
                        label={tag} 
                        size="small" 
                        variant="outlined"
                      />
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      {post.published ? `Published: ${post.published}` : 'Not published'}
                    </Typography>
                    <Box>
                      <IconButton size="small" onClick={() => handleOpen(post)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small">
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Author</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {blogPosts.map((post) => (
                <TableRow key={post.id}>
                  <TableCell>{post.title}</TableCell>
                  <TableCell>{post.category}</TableCell>
                  <TableCell>{post.author}</TableCell>
                  <TableCell>{post.published || 'Not published'}</TableCell>
                  <TableCell>
                    <Chip 
                      label={post.status} 
                      size="small" 
                      color={post.status === 'Published' ? 'success' : 'default'} 
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleOpen(post)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small">
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Post Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedPost ? 'Edit Blog Post' : 'Create New Blog Post'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Title"
              defaultValue={selectedPost?.title || ''}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Excerpt"
              multiline
              rows={2}
              defaultValue={selectedPost?.excerpt || ''}
            />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Category</InputLabel>
                  <Select
                    defaultValue={selectedPost?.category || ''}
                    label="Category"
                  >
                    {categories.map((category) => (
                      <MenuItem key={category} value={category}>
                        {category}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Status</InputLabel>
                  <Select
                    defaultValue={selectedPost?.status || 'Draft'}
                    label="Status"
                  >
                    <MenuItem value="Draft">Draft</MenuItem>
                    <MenuItem value="Published">Published</MenuItem>
                    <MenuItem value="Archived">Archived</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Featured Image URL"
              defaultValue={selectedPost?.image || ''}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Tags (comma separated)"
              defaultValue={selectedPost?.tags.join(', ') || ''}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Content"
              multiline
              rows={10}
              defaultValue=""
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleClose}>
            {selectedPost ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Blog;
